import React from 'react';
import { Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { AwsCosts as AwsCostsData, CostUnavailable } from '../../types';
import { formatDateTime } from '../../utils/format';
import { money, monthLabel } from '../../utils/costs';
import AmountBars from './AmountBars';
import { CostPanel, Figure, Figures } from './CostPanel';

const REASONS: Record<CostUnavailable['reason'], string> = {
  not_ready: 'AWS has no cost data for this account yet.',
  not_permitted: 'The API is not allowed to read the AWS bill.',
  not_configured: 'This server does not run on AWS, so there is no AWS bill to read.',
  error: 'The AWS bill could not be read.',
};

/** The AWS bill as Cost Explorer reports it: this month by service, and the months before. */
const AwsCosts: React.FC<{ aws: AwsCostsData | CostUnavailable }> = ({ aws }) => {
  if (!aws.available) {
    return (
      <Alert severity={aws.reason === 'error' || aws.reason === 'not_permitted' ? 'warning' : 'info'}>
        <strong>{REASONS[aws.reason]}</strong> {aws.detail}
      </Alert>
    );
  }
  const month = aws.this_month;
  const expected = aws.forecast_rest_of_month;
  return (
    <>
      <CostPanel
        title={`AWS, ${monthLabel(month.month)}`}
        description={`From AWS Cost Explorer, read ${formatDateTime(aws.fetched_at)}. AWS's own figures lag by up to a day.`}
      >
        <Figures label="AWS this month">
          <Figure label="Usage so far" value={money(month.usage)} hint="before credits" />
          <Figure label="Credits applied" value={money(month.credits)} />
          <Figure label="Billed so far" value={money(month.billed)} hint="usage less credits" />
          <Figure
            label="Still expected"
            value={expected === null ? 'Not known yet' : money(expected)}
            hint={expected === null ? 'AWS needs more history to forecast' : 'forecast for the rest of the month'}
          />
        </Figures>
      </CostPanel>

      <CostPanel title="By service" description="This month's usage before credits. Services under half a cent are left out.">
        <AmountBars
          label="AWS cost by service this month"
          rows={month.services.map((service) => ({ label: service.name, amount: service.amount }))}
          format={(amount) => money(amount)}
          empty="No service has cost anything yet this month."
        />
      </CostPanel>

      <CostPanel title="By month">
        <TableContainer>
          <Table size="small" aria-label="AWS cost by month" sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">Usage</TableCell>
                <TableCell align="right">Credits</TableCell>
                <TableCell align="right">Billed</TableCell>
                <TableCell>Largest service</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...aws.months].reverse().map((row) => (
                <TableRow key={row.month}>
                  <TableCell>{monthLabel(row.month)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.usage)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.credits)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.billed)}</TableCell>
                  <TableCell>{row.services[0] ? `${row.services[0].name} (${money(row.services[0].amount)})` : 'None'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CostPanel>

      {aws.daily.length > 0 && (
        <CostPanel title="By day" description="Usage before credits, the last 30 days.">
          <AmountBars
            label="AWS cost by day"
            rows={[...aws.daily].reverse().map((day) => ({ label: day.day, amount: day.amount }))}
            format={(amount) => money(amount)}
          />
        </CostPanel>
      )}
    </>
  );
};

export default AwsCosts;
